import React, { useState, useEffect } from 'react';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from 'mdi-react/CloseIcon';
import Switch from '@material-ui/core/Switch';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { withStyles } from '@material-ui/core/styles';

const PurpleSwitch = withStyles({
  switchBase: {
    color: '#A3ADC2',
    '&$checked': {
      color: '#780F9E',
    },
    '&$checked + $track': {
      backgroundColor: '#780F9E',
    },
  },
  checked: {},
  track: { backgroundColor: '#D1D6E0' },
})(Switch);

const columnGroups = [
  { key: 'general', label: 'General' },
  { key: 'building', label: 'Building / Land' },
  { key: 'financial', label: 'Financial' },
];

// Detect active theme class on the page so the portal-rendered Drawer Paper
// can be styled via the same theme CSS rules.
const getThemeClass = () => {
  if (document.querySelector('.role-bhs-agent')) return 'role-bhs-agent';
  if (document.querySelector('.role-elliman-agent')) return 'role-elliman-agent';
  return '';
};

const ColumnSelector = ({ open, onClose, visibleColumns, setVisibleColumns, allColumns }) => {
  const [draft, setDraft] = useState(visibleColumns);

  // Sync draft when drawer opens
  useEffect(() => {
    if (open) setDraft(visibleColumns);
  }, [open]);

  const handleToggle = (columnId) => {
    const col = allColumns.find(c => c.id === columnId);
    if (col && col.locked) return;

    if (draft.includes(columnId)) {
      setDraft(draft.filter(id => id !== columnId));
    } else {
      setDraft([...draft, columnId]);
    }
  };

  const handleSelectAll = () => {
    const toggleable = allColumns.filter(col => !col.locked);
    const allOn = toggleable.every(col => draft.includes(col.id));
    if (allOn) {
      setDraft(allColumns.filter(col => col.locked).map(col => col.id));
    } else {
      setDraft(allColumns.map(col => col.id));
    }
  };

  const handleResetDefault = () => {
    setDraft(allColumns.filter(col => col.default).map(col => col.id));
  };

  const handleApply = () => {
    // Always apply in allColumns order so header and body render in the same sequence
    const ordered = allColumns.filter(col => draft.includes(col.id)).map(col => col.id);
    setVisibleColumns(ordered);
    onClose();
  };

  const toggleable = allColumns.filter(col => !col.locked);
  const allSelected = toggleable.every(col => draft.includes(col.id));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        className: `column-selector-drawer ${getThemeClass()}`,
        style: { width: 300, display: 'flex', flexDirection: 'column' }
      }}
    >
      {/* Scrollable content area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Typography variant="h6" style={{ fontSize: 16, fontWeight: 600 }}>
            Customize Columns
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon size={20} />
          </IconButton>
        </div>

        <Divider style={{ marginBottom: 4 }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 0', marginBottom: 4 }}>
          <Typography variant="body2" style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
            Select All
          </Typography>
          <PurpleSwitch
            checked={allSelected}
            onChange={handleSelectAll}
            size="small"
          />
        </div>

        <Divider style={{ marginBottom: 12 }} />

        {columnGroups.map(group => {
          const groupColumns = allColumns.filter(col => col.group === group.key);
          if (groupColumns.length === 0) return null;

          return (
            <div key={group.key} style={{ marginBottom: 16 }}>
              <Typography variant="subtitle2" style={{ color: '#858AA1', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                {group.label}
              </Typography>
              {groupColumns.map(col => (
                <div
                  key={col.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '2px 0',
                  }}
                >
                  <Typography variant="body2" style={{ fontSize: 13, color: col.locked ? '#A3ADC2' : '#333' }}>
                    {col.label} {col.locked ? '(locked)' : ''}
                  </Typography>
                  <PurpleSwitch
                    checked={draft.includes(col.id)}
                    onChange={() => handleToggle(col.id)}
                    disabled={col.locked}
                    size="small"
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Sticky footer */}
      <div style={{
        padding: '26px 12px',
        borderTop: '1px solid #E0E0E0',
        background: '#fff',
        display: 'flex',
        gap: 8,
      }}>
        <Button
          className="cs-apply-btn"
          variant="contained"
          size="small"
          onClick={handleApply}
          style={{ flex: 1, background: '#780F9E', color: '#fff' }}
        >
          Apply
        </Button>
        <Button
          className="cs-reset-btn"
          variant="outlined"
          size="small"
          onClick={handleResetDefault}
          style={{ flex: 1, color: '#780F9E', borderColor: '#780F9E' }}
        >
          Reset Defaults
        </Button>
      </div>
    </Drawer>
  );
};

export default ColumnSelector;
